"use client";

import { useCallback, useState } from "react";

interface RecoveryCodesDisplayProps {
  codes: string[];
  onAcknowledge: () => void;
}

export default function RecoveryCodesDisplay({
  codes,
  onAcknowledge,
}: RecoveryCodesDisplayProps) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore clipboard failures; user can still read/copy manually
    }
  }, [codes]);

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth: 520 }}>
        <h1 className="auth-title">Save Your Recovery Codes</h1>
        <p
          style={{
            color: "var(--text-secondary)",
            fontSize: 14,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          You really, really need to save these codes somewhere secure. Each
          code works once, they cannot be shown again, and without them you
          cannot recover your account if you forget your password.
        </p>

        <div
          style={{
            border: "1px solid var(--border)",
            borderRadius: 8,
            padding: 16,
            marginBottom: 12,
            background: "var(--surface-elevated)",
            fontFamily: "ui-monospace, Menlo, monospace",
            fontSize: 13,
            lineHeight: 1.8,
            userSelect: "text",
          }}
        >
          {codes.map((code, idx) => (
            <div key={idx}>
              <span style={{ color: "var(--text-secondary)", marginRight: 8 }}>
                {String(idx + 1).padStart(2, "0")}.
              </span>
              {code}
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn-secondary"
          onClick={handleCopy}
          style={{ width: "100%", marginBottom: 16 }}
        >
          {copied ? "Copied!" : "Copy All"}
        </button>

        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 8,
            fontSize: 13,
            color: "var(--text-secondary)",
            marginBottom: 16,
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>
            I have saved these codes somewhere safe. I understand they cannot
            be shown again.
          </span>
        </label>

        <button
          type="button"
          className="btn-primary"
          disabled={!acknowledged}
          onClick={onAcknowledge}
          style={{ width: "100%" }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
